// src/pages/settings/create-organization/CreateOrganizationPlanPage.tsx
// [UI 2026-10-11] Plan step matches the upgrade-plan page. Price floats below. No structure tree.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  Brain,
  LayoutGrid,
  MessageCircle,
  Phone,
  Shield,
  Sparkles,
  Zap,
} from 'lucide-react';
import { useTranslation } from '../../../contexts/languages/LanguageContext';
import OnboardingFrame from '../../misc/onboarding/OnboardingFrame';
import { publicJourney } from '../../misc/onboarding/continuousPath';
import {
  BUILDER_CEILINGS,
  isStructureDraftReady,
  loadStructureDraft,
  structureCeilings,
  structureCounts,
} from '../../misc/onboarding/structureDraft';
import { cheapestPlanThatFits, planFits } from '../../misc/onboarding/priceBook';
import { PRICES_FAILED_LINE, usePriceBook } from '../../misc/onboarding/usePriceBook';
import { loadOrgSetup, saveOrgSetup, type BillingCycle, type OrgPlanId } from './orgSetup';
import {
  CYCLE_MONTHS,
  billingCycle,
  cycleDiscountPct,
  cycleTotalLabelKey,
  seatCount,
  seatPrice,
} from './orderQuote';
import '../../misc/onboarding/builder.css';
import './orgFlow.css';

// [PRICING] Never type a plan price in this file; the card, the floating price and the checkout all come from seatPrice()
// in orderQuote.ts (monthly, 6-month and annual tables).
const CYCLES: { id: BillingCycle; label: string }[] = [
  { id: 'monthly', label: 'settings.upgrade.billing.monthly' },
  { id: 'semester', label: 'settings.upgrade.billing.semester' },
  { id: 'annual', label: 'settings.upgrade.billing.annual' },
];
// [SPACING] Icons per plan card, indexed the same as the feature text keys settings.upgrade.plans.<id>.features.<i>.
const FEATURES: Record<OrgPlanId, React.ElementType[]> = {
  free: [LayoutGrid, Zap, MessageCircle, Phone, BarChart3, Sparkles],
  base: [MessageCircle, Phone, LayoutGrid, Zap, BarChart3, Sparkles],
  pro: [MessageCircle, Phone, Brain, BarChart3, Sparkles, Shield],
};
// Which features are ticked (true) or greyed out (false) on each card.
const INCLUDED: Record<OrgPlanId, boolean[]> = {
  free: [true, true, false, false, false, false],
  base: [true, true, true, true, true, false],
  pro: [true, true, true, true, true, true],
};
/** Elite drops two of the longer lines so the card stays on one title row. */
const SHOWN: Record<OrgPlanId, number[]> = {
  free: [0, 1, 2, 3, 4, 5],
  base: [0, 1, 2, 3, 4, 5],
  pro: [0, 1, 4, 5],
};

const CreateOrganizationPlanPage: React.FC = () => {
  // [HANDOFF] The together onboarding's plan step at /onboarding/plan, wrapped in OnboardingFrame; back goes to /onboarding?resumen=1.
  //   The logged-in create-organization flow is not on this branch (WU-139's, deferred): one flow, one return.
  const { t, language } = useTranslation();
  // [PRICING] The price book, one read per mount (usePriceBook): every price, badge, total and lock on this page comes from it (S-9).
  const { book, failed, retry } = usePriceBook();
  // The draft is read once per book state, inside the book's ceilings (S-16); his counts in the server's words for the plans' limits (S-1).
  const ceilings = useMemo(() => (book === null ? null : structureCeilings(book)) ?? BUILDER_CEILINGS, [book]);
  const draft = useMemo(() => loadStructureDraft(ceilings), [ceilings]);
  const counts = useMemo(() => structureCounts(draft), [draft]);
  const navigate = useNavigate();
  // The page renders nothing until the saved structure is confirmed finished (see the redirect below).
  const [ready, setReady] = useState(false);
  // [PRICING] The starting plan is the saved setup's; once the book is read, a plan the structure outgrows (S-1) is swapped to the
  //   cheapest plan that fits and saved (the effect below; null from the book keeps the plan).
  const [planId, setPlanId] = useState<OrgPlanId>(() => loadOrgSetup().planId);
  useEffect(() => {
    if (book === null || planFits(book, planId, counts) !== false) return;
    const next = cheapestPlanThatFits(book, counts);
    if (next === null || next === planId) return;
    setPlanId(next);
    saveOrgSetup({ ...loadOrgSetup(), planId: next });
  }, [book, counts, planId]);
  const [cycle, setCycleState] = useState<BillingCycle>(() => billingCycle(loadOrgSetup()));

  // [HANDOFF] Guard: no finished structure (name + path 2 or 3) means go back to the builder. Keeps people from deep-linking here.
  useEffect(() => {
    if (!isStructureDraftReady(draft)) {
      navigate('/onboarding', { replace: true });
      return;
    }
    setReady(true);
  }, [navigate, draft]);

  if (!ready) return null;

  const money = (n: number) => n.toLocaleString(language === 'es' ? 'es-MX' : 'en-US');
  // [PRICING] Per seat per month from the book's row; null until the book is read or when the row is missing (THE FAILED READ, S-9).
  const shown = (id: OrgPlanId) => (book === null ? null : seatPrice(book, id, cycle));
  // [PRICING] Bottom bar = seats only (no extensions yet) for the chosen cycle: seats x per-seat price x months.
  // Monthly "al mes"; 6 months / annual "por 6 / 12 meses". Same number as "Total del plan" (quote.planDue) at checkout.
  const seats = seatCount(draft);
  const planSeat = shown(planId);
  const seatTotal = planSeat === null ? null : seats * planSeat * CYCLE_MONTHS[cycle];

  // [HANDOFF] Choices are saved straight to localStorage (ventaje_org_setup_v1) as they are made: planId and cycle
  // (`yearly` is kept in sync for older readers).
  const pick = (next: OrgPlanId) => {
    setPlanId(next);
    saveOrgSetup({ ...loadOrgSetup(), planId: next });
  };

  const setCycle = (next: BillingCycle) => {
    setCycleState(next);
    saveOrgSetup({ ...loadOrgSetup(), cycle: next, yearly: next === 'annual' });
  };

  const choices = (
    <>
      <div className="mb-6 flex flex-col items-center gap-2">
        <div className="flex flex-wrap items-center justify-center gap-2.5">
          {CYCLES.map((c) => (
            <button key={c.id} type="button" className={`org-cycle${cycle === c.id ? ' on' : ''}`} onClick={() => setCycle(c.id)}>
              {t(c.label)}
            </button>
          ))}
        </div>
        {/* [PRICING] THE FAILED READ (S-9): the one line in place of the numbers, above the cards; it is the retry. The gold button waits for a read. */}
        {failed ? (
          <button type="button" className="ob-tag underline" onClick={retry}>
            {PRICES_FAILED_LINE}
          </button>
        ) : null}
      </div>
      {/* [SPACING] Three cards: 1 column on mobile, 3 columns from md, gap-8. mb-6 above separates them from the billing-cycle toggle. */}
      {/* [PRICING] 6 months / annual show the lower per-person price (still per month) plus a "-NN%" badge computed from the tables. */}
      <div className="org-plan-grid grid grid-cols-1 md:grid-cols-3 gap-8">
        {(['free', 'base', 'pro'] as OrgPlanId[]).map((id) => {
          // [PRICING] A plan the structure outgrows is locked (S-1); null while the book is unread reads as not locked, so no lock note flashes.
          const locked = book !== null && planFits(book, id, counts) === false;
          const price = shown(id);
          const pct = book === null ? null : cycleDiscountPct(book, id, cycle);
          const on = planId === id && !locked;
          return (
            <button key={id} type="button" disabled={locked} onClick={() => { if (!locked) pick(id); }} className={`org-plan-card tone-${id}${on ? ' on' : ''}${locked ? ' is-locked' : ''}`}>
              <h3>{t(`settings.upgrade.plans.${id}.name`)}</h3>
              <p className="org-plan-muted">{t(`settings.upgrade.plans.${id}.description`)}</p>
              <p className="org-plan-price">
                {price === null ? null : (
                  <>
                    ${money(price)}
                    <span> MXN / {t('settings.createOrganization.perPerson')}</span>
                  </>
                )}
                {cycle !== 'monthly' && pct !== null ? <em className="org-plan-savepct">−{pct}%</em> : null}
              </p>
              <ul>
                {SHOWN[id].map((i) => {
                  const Icon = FEATURES[id][i];
                  return (
                    <li key={i} className={INCLUDED[id][i] ? '' : 'off'}>
                      <Icon size={18} />
                      <span>{t(`settings.upgrade.plans.${id}.features.${i}`)}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="org-plan-limits">{t(`settings.upgrade.plans.${id}.limits`)}</p>
              {locked ? <p className="org-plan-locknote">{t('settings.createOrganization.planTooSmall')}</p> : null}
            </button>
          );
        })}
      </div>
    </>
  );

  const foot = (
    <div className="org-float">
      <button
        type="button"
        className="ob-back"
        onClick={() => navigate('/onboarding?resumen=1')}
      >
        {t('settings.createOrganization.back')}
      </button>
        <div className="org-float-price">
          <span>{t(`settings.upgrade.plans.${planId}.name`)}</span>
          <b className="tabular-nums">{seatTotal === null ? null : `$${money(seatTotal)} MXN`}</b>
          <span>{t(cycleTotalLabelKey(cycle))}</span>
        </div>
      <button
        type="button"
        className="btn-gold inline-flex items-center justify-center min-h-[48px] px-6 rounded-xl text-sm font-bold disabled:cursor-not-allowed disabled:opacity-60"
        disabled={book === null}
        onClick={() => navigate('/onboarding/extensiones')}
      >
        {t('settings.createOrganization.continueExtras')}
      </button>
    </div>
  );

  const progress = publicJourney(draft.path, 'plan');
  return (
    <OnboardingFrame step={progress.step} total={progress.total} foot={foot}>
      <div className="ob-flow-copy">
        <h1 className="ob-flow-title">{t('settings.upgrade.title')}</h1>
        <p className="ob-flow-sub">{t('settings.upgrade.subtitle')}</p>
      </div>
      {choices}
    </OnboardingFrame>
  );
};

export default CreateOrganizationPlanPage;
